import { useState, useEffect } from 'react';
import birdLogo from '../../../assets/matou-bird-logo-grey.svg';
import textLogo from '../../../assets/matou-text-logo-white.svg';

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '#about' },
    { label: 'Journey', href: '#journey' },
    { label: 'Strategy', href: '#strategy' },
    { label: 'Contributors', href: '#contributors' },
    { label: 'Services', href: '#services' },
    { label: 'Products', href: '#products' },
    { label: 'Events', href: '#events' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    setJoinOpen(false);
    if (href.startsWith('#')) {
      const el = document.getElementById(href.slice(1));
      el?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-matou-teal-dark/95 backdrop-blur-sm shadow-lg' : 'bg-matou-teal-dark'
      }`}
    >
      <div className="max-w-content mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-3"
          >
            <img src={birdLogo} alt="Mātou Bird" className="h-6" />
            <img src={textLogo} alt="Mātou Collective" className="h-5" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                className="font-mono text-sm text-white/80 hover:text-white transition-colors duration-150"
              >
                {link.label}
              </a>
            ))}

            <a
              href="https://docs.matou.nz"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm text-white/80 hover:text-white transition-colors duration-150"
            >
              Docs
            </a>

            {/* Join Us Dropdown */}
            <div className="relative">
              <button
                onClick={() => setJoinOpen(!joinOpen)}
                className="font-mono text-sm bg-matou-teal-medium text-white px-4 py-2 rounded-md hover:bg-matou-teal-soft hover:text-matou-teal-dark transition-all duration-150 flex items-center gap-1"
              >
                Join Us
                <svg className={`w-3 h-3 transition-transform ${joinOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {joinOpen && (
                <>
                  <div className="fixed inset-0" onClick={() => setJoinOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-matou-teal-soft/30 overflow-hidden">
                    <a
                      href="/join"
                      className="block px-4 py-3 font-mono text-sm text-matou-text hover:bg-matou-bg transition-colors"
                    >
                      Join with Email
                    </a>
                    <a
                      href="https://matou.nz/downloads"
                      className="block px-4 py-3 font-mono text-sm text-matou-text hover:bg-matou-bg transition-colors border-t border-gray-100"
                    >
                      Join with App
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-white/10">
            <div className="flex flex-col gap-1 pt-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                  className="font-mono text-sm text-white/80 hover:text-white py-2 px-3 rounded transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="https://docs.matou.nz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-sm text-white/80 hover:text-white py-2 px-3 rounded transition-colors"
              >
                Docs
              </a>
              <div className="border-t border-white/10 mt-2 pt-2">
                <p className="font-mono text-xs text-white/50 px-3 py-1 uppercase tracking-wider">Join Us</p>
                <a
                  href="/join"
                  className="font-mono text-sm text-matou-teal-soft hover:text-white py-2 px-3 rounded transition-colors block"
                >
                  Join with Email
                </a>
                <a
                  href="https://matou.nz/downloads"
                  className="font-mono text-sm text-matou-teal-soft hover:text-white py-2 px-3 rounded transition-colors block"
                >
                  Join with App
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
